import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Divider,
  Stack,
  Chip,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@mui/material';
import { format } from 'date-fns';
import { getJob, getTimeslotsByJobId, getRegistrationsForJob } from '../services/api';
import { Job, Timeslot, Registration } from '../models/types';

const JobDetailAdminPage: React.FC = () => {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  
  const [job, setJob] = useState<Job | null>(null);
  const [timeslots, setTimeslots] = useState<Timeslot[]>([]);
  const [registrations, setRegistrations] = useState<Registration[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!jobId) return;
      
      try {
        setLoading(true);
        
        const jobData = await getJob(jobId);
        setJob(jobData);
        
        const timeslotsData = await getTimeslotsByJobId(jobId);
        setTimeslots(timeslotsData.sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime()));
        
        const registrationsData = await getRegistrationsForJob(jobId);
        setRegistrations(registrationsData.sort((a, b) => new Date(a.registeredAt).getTime() - new Date(b.registeredAt).getTime()));
        
      } catch (err) {
        console.error('Error fetching job details:', err);
        setError('Failed to load job details. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [jobId]);

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>          Lade Job-Details...
        </Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Button variant="contained" onClick={() => navigate('/admin')}>
            Zurück zum Admin-Dashboard
          </Button>
        </Box>
      </Container>
    );
  }

  if (!job) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">Job nicht gefunden</Alert>
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Button variant="contained" onClick={() => navigate('/admin')}>
            Zurück zum Admin-Dashboard
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Button
              variant="outlined"
              onClick={() => navigate('/admin')}
              sx={{ mb: 1 }}
            >
              Zurück zur Jobliste
            </Button>
            <Typography variant="h4">
              {job.name}
            </Typography>
          </Box>
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate(`/admin/jobs/edit/${job.id}`)}
          >
            Job bearbeiten
          </Button>
        </Box>

        <Paper sx={{ p: 3, mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Job-Details
          </Typography>
          <Divider sx={{ mb: 2 }} />
          
          <Stack spacing={2}>            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Ort:</strong> {job.location}
                </Typography>
              </Box>
              
              {job.responsible && (
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Verantwortlich:</strong> {job.responsible}
                  </Typography>
                </Box>
              )}
              
            </Stack>
            
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Benötigte Helfer:</strong> {job.requiredVolunteers} (berechnet aus Zeitfenstern)
                </Typography>
              </Box>
  
              
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Status:</strong>{' '}
                  {job.isActive ? (                    <Chip label="Aktiv" color="success" size="small" />
                  ) : (
                    <Chip label="Inaktiv" color="default" size="small" />
                  )}
                </Typography>
              </Box>
            </Stack>
            
            <Box>
              <Typography variant="body2" sx={{ mt: 1, mb: 1 }}>                <strong>Beschreibung:</strong>
              </Typography>
              <Typography variant="body1" paragraph>
                {job.description}
              </Typography>
            </Box>
          </Stack>
        </Paper>

        <Paper sx={{ p: 3, mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">              Zeitfenster
            </Typography>
            <Button
              variant="contained"
              size="small"
              onClick={() => navigate(`/admin/jobs/${job.id}/timeslots/create`)}
            >
              Zeitfenster hinzufügen
            </Button>
          </Box>
          <Divider sx={{ mb: 2 }} />
          
          {timeslots.length === 0 ? (
            <Alert severity="info">
              Für diesen Job wurden noch keine Zeitfenster erstellt.
            </Alert>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Beschreibung</TableCell>
                    <TableCell>Startzeit</TableCell>
                    <TableCell>Endzeit</TableCell>
                    <TableCell>Benötigte Helfer</TableCell>
                    <TableCell align="right">Aktionen</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {timeslots.map((timeslot) => (
                    <TableRow key={timeslot.id}>
                      <TableCell><strong>{timeslot.description || 'Keine Beschreibung'}</strong></TableCell>
                      <TableCell>{format(new Date(timeslot.startTime), 'MMM d, H:mm')}</TableCell>
                      <TableCell>{format(new Date(timeslot.endTime), 'H:mm')}</TableCell>
                      <TableCell>{timeslot.requiredVolunteers}</TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          onClick={() => navigate(`/admin/timeslots/edit/${timeslot.id}`)}
                          sx={{ mr: 1 }}
                        >
                          Bearbeiten
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>

        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Helfer-Anmeldungen
          </Typography>
          <Divider sx={{ mb: 2 }} />
          
          {registrations.length === 0 ? (
            <Alert severity="info">
              Für diesen Job haben sich noch keine Helfer angemeldet.
            </Alert>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>                    <TableCell>Helfer</TableCell>
                    <TableCell>Zeitfenster</TableCell>
                    <TableCell>Anmeldedatum</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Gearbeitete Minuten</TableCell>
                    <TableCell align="right">Aktionen</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {registrations.map((registration) => (
                    <TableRow key={registration.id}>
                      <TableCell>{registration.volunteerName}</TableCell>
                      <TableCell>{format(new Date(registration.timeslotStart || ''), 'MMM d, h:mm a')}</TableCell>
                      <TableCell>{format(new Date(registration.registeredAt), 'MMM d, yyyy')}</TableCell>
                      <TableCell>                        {registration.hasAttended ? (
                          <Chip label="Anwesend" color="success" size="small" />
                        ) : registration.isConfirmed ? (
                          <Chip label="Bestätigt" color="primary" size="small" />
                        ) : (
                          <Chip label="Ausstehend" color="warning" size="small" />
                        )}
                      </TableCell>                      <TableCell>{registration.actualMinutesWorked || 'Nicht erfasst'}</TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          onClick={() => navigate(`/admin/registrations/edit/${registration.id}`)}
                        >
                          Bearbeiten
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default JobDetailAdminPage;

